import {Grid, Tabs, Typography, UnstableCard as Paper} from '@logistics-frontend/blocks'
import {NumberInput} from '@yandex-delivery/form'
import React, {memo} from 'react'

import {Badge, Body, BoxIcon, Header, ToggleTitle} from 'src/shared/components/desktop'
import {i18n} from 'src/shared/config'

import {BusinessBannerModal} from '../BusinessBannerModal/BusinessBannerModal'
import {PredefinedParcelSelect} from '../DestinationPoints/PredefinedParcelSelect/PredefinedParcelSelect'
import {BUSINESS_PARCEL_SIZES, OrderParcelSize, parcelEntries} from './consts'
import {useParcel} from './hooks'
import {translations} from './i18n'
import * as classes from './Parcel.classes'
import type {ParcelSizes, Props} from './types'

export const Parcel = memo<Props>(function Parcel(props) {
  const {
    parcelSizes,
    isCustomSizeOpened,
    customSizeVisibilityHandler,
    selectedParcelSize,
    sizeSetHandler,
    isBannerVisible,
    toggleBannerVisibilityHandler,
    selectSegmentHandler,
    bannerActionButtonClickHandler,
  } = useParcel(getParcelSizes, props)

  return (
    <>
      <Paper radius="L">
        <Grid size="m" container>
          <Grid xs={12} item>
            <Header lead={<BoxIcon />}>{i18n('ndd.parcels.title', 'Отправление')}</Header>
          </Grid>
          <Grid xs={12} item>
            <Body
              lead={
                <React.Fragment>
                  <Typography group="tight" thickness="medium" variant="body2">
                    {i18n('ndd.delivery-method.field.cargo-size.title', 'Размер посылки')}
                  </Typography>
                  <ToggleTitle
                    isOpened={isCustomSizeOpened}
                    onClick={customSizeVisibilityHandler}
                    title={i18n('form.order.sizes.custom.sub-title', 'Ввести свои размеры')}
                  />
                </React.Fragment>
              }
              tail={
                <React.Fragment>
                  <Tabs.Segments items={parcelSizes} onChange={sizeSetHandler} size="l" value={selectedParcelSize} />
                  {isCustomSizeOpened && (
                    <Grid size="xs" container>
                      <Grid item xs>
                        <NumberInput
                          label={i18n('ndd.form.item.size.length', 'Длина, см')}
                          min="0"
                          name={`${props.name}.dimensions.length`}
                          size="l"
                          step="0.1"
                          type="number"
                          variant="line"
                        />
                      </Grid>
                      <Grid item xs>
                        <NumberInput
                          label={i18n('ndd.form.item.size.width', 'Ширина, см')}
                          min="0"
                          name={`${props.name}.dimensions.width`}
                          size="l"
                          step="0.1"
                          type="number"
                          variant="line"
                        />
                      </Grid>
                      <Grid item xs>
                        <NumberInput
                          label={i18n('ndd.form.item.size.height', 'Высота, см')}
                          min="0"
                          name={`${props.name}.dimensions.height`}
                          size="l"
                          step="0.1"
                          type="number"
                          variant="line"
                        />
                      </Grid>
                      <Grid item xs>
                        <NumberInput
                          label={i18n('ndd.form.item.weight', 'Вес, кг')}
                          min="0"
                          name={`${props.name}.dimensions.weight`}
                          size="l"
                          step="0.1"
                          type="number"
                          variant="line"
                        />
                      </Grid>
                    </Grid>
                  )}
                </React.Fragment>
              }
            />
          </Grid>
          <Grid xs={12} item>
            <PredefinedParcelSelect data-testid={`parcels_${props.name}.products.0.name`} name={`${props.name}.products.0.name`} onChange={selectSegmentHandler} />
          </Grid>
        </Grid>
      </Paper>
      <BusinessBannerModal
        description={translations.description}
        imageSrc={translations.imageSrc}
        list={translations.list}
        onActionButtonClick={bannerActionButtonClickHandler}
        onClose={toggleBannerVisibilityHandler}
        opened={isBannerVisible}
        title={translations.title}
        utmSource="order_cargo"
      />
    </>
  )
})

function getParcelSizes(selectedSize: OrderParcelSize, isBusiness: boolean) {
  return parcelEntries.reduce<ParcelSizes>((acc, [size, value]) => {
    const isBusinessSize = BUSINESS_PARCEL_SIZES.includes(size)

    // не показываем бизнес размеры, если не выбран чекбокс "бизнес"
    if (isBusinessSize && !isBusiness) {
      return acc
    }

    acc.push({
      value: size,
      label: (
        <Grid alignItems="center" direction="column" container>
          <Grid alignItems="center" justify="center" wrap="nowrap" container item xs>
            <Grid item>
              <Typography color={selectedSize === size ? 'main' : 'minor'} group="tight" variant="body2">
                {value.title}
              </Typography>
            </Grid>
            {isBusinessSize && (
              <Grid className={classes.businessBadge} item>
                <Badge label={i18n('form.order.badge.business', 'Бизнес')} isDisabled />
              </Grid>
            )}
          </Grid>

          {value.weightTitle && (
            <Typography
              className={classes.sizeSubTitle}
              color="minor"
              Component={Grid}
              group="tight"
              variant="caption2"
              item
            >
              {value.weightTitle}
            </Typography>
          )}
          {value.subtitle && (
            <Typography
              className={classes.sizeSubTitle}
              color="minor"
              Component={Grid}
              group="tight"
              variant="caption2"
              item
            >
              {value.subtitle}
            </Typography>
          )}
        </Grid>
      ),
    })

    return acc
  }, [])
}
